import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import {
    LedgerIntelligenceCellValue,
    LedgerIntelligenceCheck,
    LedgerIntelligenceHighlight,
    LedgerIntelligenceMoneyCell,
    LedgerIntelligenceResult,
    LedgerIntelligenceTable,
    LedgerIntelligenceWarning,
} from '../../../../services/gable/gable.service';

/**
 * Inline rendering of the `analyze_ledger` tool result inside the Gable
 * chat — the AUTHORITATIVE numeric display for a Ledger Intelligence
 * answer. The model's prose above/below this card is commentary only;
 * every number a user can act on lives here, server-formatted.
 *
 * Renders verbatim `formattedValue`/`formattedDifference` strings from the
 * server — NEVER divides cents by 100 or reformats money client-side, so
 * the card can never drift from the ledger it's reporting on.
 *
 * Only ever mounted for `status === 'answered'` (the host narrows on the
 * tool-card guard before binding `[payload]`), so there's no loading/empty
 * state to render here.
 */
@Component({
    selector: 'app-gable-ledger-intelligence-card',
    templateUrl: './ledger-intelligence-card.component.html',
    styleUrls: ['./ledger-intelligence-card.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [CommonModule],
})
export class GableLedgerIntelligenceCardComponent {
    @Input() payload: LedgerIntelligenceResult | null = null;

    get title(): string {
        return this.payload?.title ?? 'Ledger analysis';
    }

    get subtitle(): string | null {
        return this.payload?.subtitle ?? null;
    }

    get periodLabel(): string | null {
        return this.payload?.period?.label ?? null;
    }

    get highlights(): LedgerIntelligenceHighlight[] {
        return this.payload?.highlights ?? [];
    }

    get checks(): LedgerIntelligenceCheck[] {
        return this.payload?.checks ?? [];
    }

    get tables(): LedgerIntelligenceTable[] {
        return this.payload?.tables ?? [];
    }

    get warnings(): LedgerIntelligenceWarning[] {
        return this.payload?.warnings ?? [];
    }

    checkBadge(status: LedgerIntelligenceCheck['status']): 'completed' | 'failed' | 'disabled' {
        return status === 'passed' ? 'completed' : status === 'failed' ? 'failed' : 'disabled';
    }

    checkLabel(status: LedgerIntelligenceCheck['status']): string {
        return status === 'passed' ? 'Passed' : status === 'failed' ? 'Failed' : 'Not run';
    }

    tableColumns(table: LedgerIntelligenceTable): { key: string; header: string; type: 'text'; align?: 'right' }[] {
        return table.columns.map((c) => ({
            key: c.key,
            header: c.label,
            type: 'text' as const,
            ...(c.type === 'money' || c.type === 'count' ? { align: 'right' as const } : {}),
        }));
    }

    tableRows(table: LedgerIntelligenceTable): Record<string, string>[] {
        return table.rows.map((row, i) => {
            const out: Record<string, string> = { id: String(i) };
            for (const c of table.columns) {
                const v = row[c.key];
                out[c.key] = this.isMoneyCell(v) ? v.formattedValue : v === null || v === undefined ? '' : String(v);
            }
            return out;
        });
    }

    openCsv(url: string): void {
        window.open(url, '_blank', 'noopener');
    }

    /** True for a money cell object (`{valueCents, formattedValue}`) vs. a raw text/number cell. */
    isMoneyCell(value: LedgerIntelligenceCellValue): value is LedgerIntelligenceMoneyCell {
        return typeof value === 'object' && value !== null;
    }

    cellValue(row: Record<string, LedgerIntelligenceCellValue>, columnKey: string): LedgerIntelligenceCellValue {
        // Casing-robust lookup: the response middleware (CamelCaseResponseKeys)
        // camelCases nested payload keys, so a snake_case column key like
        // 'batch_id'/'entry_date' won't match the camelCased row key
        // ('batchId'/'entryDate') and the column would render blank. Try the
        // key as-is, then its camelCase and snake_case variants, so a
        // contract-casing drift can never silently blank a diagnostic column
        // (SD-8058 P2 — the third time this nested-payload trap has bitten a card).
        for (const key of [columnKey, this.toCamel(columnKey), this.toSnake(columnKey)]) {
            if (row[key] !== undefined) {
                return row[key] ?? null;
            }
        }
        return null;
    }

    private toCamel(key: string): string {
        return key.replace(/_([a-z0-9])/g, (_match, c: string) => c.toUpperCase());
    }

    private toSnake(key: string): string {
        return key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
    }

    trackByHighlightKey(_index: number, highlight: LedgerIntelligenceHighlight): string {
        return highlight.key;
    }

    trackByCheckKey(_index: number, check: LedgerIntelligenceCheck): string {
        return check.key;
    }

    trackByTableKey(_index: number, table: LedgerIntelligenceTable): string {
        return table.key;
    }

    trackByWarningKey(_index: number, warning: LedgerIntelligenceWarning): string {
        return warning.key;
    }

    trackByColumnKey(_index: number, column: { key: string }): string {
        return column.key;
    }

    trackByRowIndex(index: number): number {
        return index;
    }
}
